import { ErrorResponse } from '@shared/types'
import { useActor } from '@ui-common/hooks/useActor'
import { shallowEqual, useSelector } from '@xstate/react'
import { useCallback } from 'react'
import { AnyStateMachine, BaseActorRef, MachineContext, MachineImplementationsFrom } from 'xstate'

import { formDataStepMachine, FormStepActions, SUBMIT_FAILED, SUBMITTING } from './formDataStepMachine'
import { PromptEvent } from './promptMachine'
import { EditableMachineContext, ErrorMachineContext } from './types'

export interface FormStepState<
  TContext extends MachineContext & EditableMachineContext & ErrorMachineContext<TError>,
  TStepOutputDataType,
  TError = unknown,
> {
  context: TContext
  submit: (data: TStepOutputDataType) => void
  abort: () => void
  showInfo: () => void
  back: (hasUnsavedChanges: boolean) => void
  isSubmitting: boolean
  hasSubmitFailed: boolean
  abortPromptMachine?: BaseActorRef<PromptEvent>
  infoPromptMachine?: BaseActorRef<PromptEvent>
  reEditPromptMachine?: BaseActorRef<PromptEvent>
  error?: ErrorResponse<TError>
}

export const useFormStepState = <
  TContext extends EditableMachineContext & ErrorMachineContext<TErrorCode>,
  TStepOutputDataType extends object,
  TErrorCode = unknown,
>(
  input: TContext,
  parentActions: FormStepActions<TStepOutputDataType>,
  machineImplementations?: Partial<MachineImplementationsFrom<AnyStateMachine>>,
): FormStepState<TContext, TStepOutputDataType, TErrorCode> => {
  const machine = formDataStepMachine<TContext, TStepOutputDataType, TErrorCode>(parentActions).provide(
    machineImplementations ?? {},
  )

  const actor = useActor(machine, { input })

  const currentState = useSelector(actor, (snapshot) => snapshot.value)
  const context = useSelector(actor, (snapshot) => snapshot.context, shallowEqual)
  const childActors = useSelector(actor, (snapshot) => snapshot.children, shallowEqual)
  const { abortPromptMachine, infoPromptMachine, reEditPromptMachine } = childActors
  const isSubmitting = currentState === SUBMITTING
  const hasSubmitFailed = currentState === SUBMIT_FAILED

  const back = useCallback(
    (hasUnsavedChanges: boolean = false) => {
      if (context.isEditing && hasUnsavedChanges) {
        actor.send({ type: 'SHOW_CANCEL_REEDIT_PROMPT' })
      } else {
        actor.send({ type: 'BACK' })
      }
    },
    [actor, context.isEditing],
  )

  return {
    context,
    abort: () => actor.send({ type: 'SHOW_ABORT_PROMPT' }),
    showInfo: () => actor.send({ type: 'SHOW_INFO_PROMPT' }),
    back,
    submit: (data: TStepOutputDataType) => actor.send({ type: 'SUBMIT', data }),
    isSubmitting,
    hasSubmitFailed,
    abortPromptMachine,
    infoPromptMachine,
    reEditPromptMachine,
    error: context.error,
  }
}
